import * as React from 'react';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import TitledContainer from '../../components/TitledContainer';
import { useDispatch, useSelector } from 'react-redux';
import { getSettings } from '../../store/settings/selectors';
import { useCallback, useState } from 'react';
import {
    loadSettings,
    updateGeneralSettings,
    updateReportsSettings,
    updateRemindersSettings,
    updatePaymentsSettings,
    updateExtrasSettings,
    updateClientsLoginSettings,
    updateIntegrationsSettings,
    updateAlertsSettings,
    updateTaskManagementSettings,
    updateInvoicesSettings,
    updateConsentManagementSettings,
    updateCashRegisterSettings,
    updateWaitingListSettings,
    updatePrescriptionsSettings,
} from '../../store/settings/actions';
import { getFeatures } from '../../store/session/selectors';
import { FeatureEnum } from '../../models';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { alertError } from '../../store/alerts/actions';
import { loadEntities } from '../../store/entities/actions';
import { getStockAsync, downloadConsentPdfAsync } from '../../ajax/requests';
import { useTaxData } from '../../hooks/useTaxData';
import { useHistory, useParams } from 'react-router-dom';
import { paths } from '../../app/constants';
import Breadcrumbs from '../../components/Breadcrumbs';
import SettingsMenu, { SettingItemId, settingsItems } from './SettingsMenu';
import SettingsItem from './SettingsItem';
import GeneralSettings from './GeneralSettings';
import ServicesSettings from './ServicesSettings';
import RemindersSettings from './RemindersSettings';
import ExtrasSettings from './ExtrasSettings';
import ReportsAndDocumentsSettings from './ReportsAndDocumentsSettings';
import PacksSettings from './PacksSettings';
import PaymentProfilesSettings from './PaymentsSettings/PaymentProfilesSettings';
import ClientsLoginSettings from './ClientLoginSettings';
import ConsentManagementSettings from './ConsentManagementSettings';
import ClientsIntelligentCategorizationSettings from './ClientsIntelligentCategorizationSettings';
import InvoicesSettings from './InvoicesSettings';
import { GoHighLevelSettings } from './IntegrationsSettings/GoHighLevelSettings';
import { GoogleCalendarSettings } from './IntegrationsSettings/GoogleCalendarSettings';
import { MailerLiteSettings } from './IntegrationsSettings/MailerLiteSettings';
import { WebhookSettings } from './IntegrationsSettings/WebhookSettings';
import { AutomaticPaymentsSettings } from './PaymentsSettings/AutomaticPaymentsSettings';
import CashRegisterSettings from './CashRegisterSettings';
import UsersSettings from './UsersSettings';
import TaskManagementSettings from './TaskManagementSettings';
import ProfessionalRemindersSettings from './ProfessionalRemindersSettings';
import SurveysSettings from './SurveysSettings';
import WaitingListSettings from './WaitingListSettings';
import AppButton from '../../components/AppButton';
import PrescriptionsSettings from './PrescriptionsSettings';

interface SettingsParams {
    settingId?: SettingItemId;
}

const Settings: React.FC = () => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const history = useHistory();
    const { settingId } = useParams<SettingsParams>() as SettingsParams;
    const { isExemptTax } = useTaxData();

    const features = useSelector(getFeatures);
    const settings = useSelector(getSettings);

    const [showProviderConfirm, setShowProviderConfirm] = useState(false);
    const [pendingInvoiceSettings, setPendingInvoiceSettings] = useState<any>(null);

    const goBackToSettings = useCallback(() => {
        history.push(paths.settings());
    }, [history]);

    const forceReloadSettings = useCallback(() => {
        dispatch(loadSettings());
        // Load stock once for all child components
        if (features.includes(FeatureEnum.Stock)) {
            dispatch(loadEntities('stock', () => getStockAsync(), 'NewResult'));
        }
    }, [dispatch, features]);

    React.useEffect(() => {
        forceReloadSettings();
    }, []);

    const onSaveGeneral = useCallback(settings => dispatch(updateGeneralSettings(settings)), [dispatch]);
    const onSaveExtras = useCallback(settings => dispatch(updateExtrasSettings(settings)), [dispatch]);
    const onSaveReports = useCallback(settings => dispatch(updateReportsSettings(settings)), [dispatch]);
    const onSaveReminders = useCallback(settings => dispatch(updateRemindersSettings(settings)), [dispatch]);
    const onSaveTaskManagement = useCallback(settings => dispatch(updateTaskManagementSettings(settings)), [dispatch]);
    const onSaveAlerts = useCallback(settings => dispatch(updateAlertsSettings(settings)), [dispatch]);
    const onSavePayments = useCallback(settings => dispatch(updatePaymentsSettings(settings)), [dispatch]);
    const onSaveClientsLogin = useCallback(settings => dispatch(updateClientsLoginSettings(settings)), [dispatch]);
    const onSaveIntegrations = useCallback(settings => dispatch(updateIntegrationsSettings(settings)), [dispatch]);
    const onSaveConsentManagement = useCallback(settings => dispatch(updateConsentManagementSettings(settings)), [dispatch]);
    const onSaveCashRegister = useCallback(settings => dispatch(updateCashRegisterSettings(settings)), [dispatch]);
    const onSaveWaitingList = useCallback(settings => dispatch(updateWaitingListSettings(settings)), [dispatch]);
    const onSavePrescriptions = useCallback(settings => dispatch(updatePrescriptionsSettings(settings)), [dispatch]);

    const handleDownloadConsentPdf = useCallback(async () => {
        try {
            const blob = await downloadConsentPdfAsync();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'consent-form.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            dispatch(alertError(t(TK.somethingWentWrong)));
        }
    }, [dispatch, t]);

    const onSaveInvoices = useCallback(newSettings => {
        const legalInfo = newSettings.companyLegalInfo;

        // Clean up tax exemption fields if the tax is not exempt (rate !== 0%)
        const cleanedSettings = {
            ...newSettings,
            companyLegalInfo: {
                ...legalInfo,
                taxExemption: isExemptTax(legalInfo?.taxId) ? legalInfo.taxExemption : undefined,
                taxExemptionLaw: isExemptTax(legalInfo?.taxId) ? legalInfo.taxExemptionLaw : undefined
            }
        };

        if (settings.invoices?.provider !== undefined && cleanedSettings.provider !== settings.invoices.provider) {
            setPendingInvoiceSettings(cleanedSettings);
            setShowProviderConfirm(true);
        } else {
            dispatch(updateInvoicesSettings(cleanedSettings));
        }
    }, [dispatch, settings.invoices, isExemptTax]);


    if (!settingId) {
        return (
            <>
                <Breadcrumbs items={[
                    { label: t(TK.settings), active: true }
                ]} />
                <SettingsMenu />
            </>
        );
    }

    const breadcrumbItems = [
        { label: t(TK.settings), active: false, onClick: goBackToSettings },
        { label: t(settingsItems[settingId].categoryTitleKey), active: false },
        { label: t(settingsItems[settingId].titleKey), active: true }
    ];

    const renderContent = () => {

        switch (settingId as SettingItemId) {
            case 'general':
                return <SettingsItem
                            id='general'
                            title={t(TK.general)}
                            contentComponentType={GeneralSettings}
                            values={settings.general}
                            onSave={onSaveGeneral}
                        />
            case 'users':
                return <SettingsItem
                            id='users'
                            title={t(TK.users)}
                            contentComponentType={UsersSettings}
                            values={null}
                            onSave={() => {}}
                            hideButtons
                        />
            case 'services':
                return <SettingsItem
                            id='services'
                            title={t(TK.services)}
                            contentComponentType={ServicesSettings}
                            values={settings.general}
                            onSave={onSaveGeneral}
                            hideButtons
                        />
            case 'reminders':
                return <SettingsItem
                            id='reminders'
                            title={t(TK.clientReminders)}
                            contentComponentType={RemindersSettings}
                            values={settings.reminders}
                            onSave={onSaveReminders}
                        />
            case 'professional-reminders':
                return <SettingsItem
                            id='professional-reminders'
                            title={t(TK.professionalReminders)}
                            contentComponentType={ProfessionalRemindersSettings}
                            values={settings.alerts}
                            onSave={onSaveAlerts}
                        />
            case 'task-management':
                return <SettingsItem
                            id='task-management'
                            title={t(TK.taskManagement)}
                            contentComponentType={TaskManagementSettings}
                            values={settings.taskManagement ?? {}}
                            onSave={onSaveTaskManagement}
                        />
            case 'waiting-list':
                return <SettingsItem
                            id='waiting-list'
                            title={t(TK.waitingListSettings)}
                            contentComponentType={WaitingListSettings}
                            values={settings.waitingListSettings}
                            onSave={onSaveWaitingList}
                        />
            case 'extras':
                return <SettingsItem
                            id='extras'
                            title={t(TK.extrasAndProducts)}
                            contentComponentType={ExtrasSettings}
                            values={settings.extras}
                            onSave={onSaveExtras}
                            hideButtons
                        />
            case 'reports':
                return <ReportsAndDocumentsSettings
                            id='reports-and-documents'
                            reportsSettingsData={settings.reports}
                            onSaveReports={onSaveReports}
                        />
            case 'packs':
                return <SettingsItem
                            id='packs'
                            title={`${t(TK.packs)}/${t(TK.subscriptions)}`}
                            contentComponentType={PacksSettings}
                            values={null}
                            onSave={() => {}}
                            hideButtons
                        />
            case 'payment-profiles':
                return <SettingsItem
                            id='payments'
                            title={t(TK.paymentProfiles)}
                            contentComponentType={PaymentProfilesSettings}
                            values={settings.payments}
                            onSave={onSavePayments}
                            hideButtons
                        />
            case 'automatic-payments':
                return <SettingsItem
                            id='automatic-payments'
                            title={t(TK.automaticPayments)}
                            contentComponentType={AutomaticPaymentsSettings}
                            values={settings.payments}
                            onSave={onSavePayments}
                        />
            case 'cash-register':
                return <SettingsItem
                            id='cash-register'
                            title={t(TK.cashRegister)}
                            contentComponentType={CashRegisterSettings}
                            values={settings.cashRegisterSettings}
                            onSave={onSaveCashRegister}
                        />
            case 'clients-login':
                return <SettingsItem
                            id='clients-login'
                            title={t(TK.clientsPortalsAndMintu)}
                            contentComponentType={ClientsLoginSettings}
                            values={settings.clientsLogin}
                            onSave={onSaveClientsLogin}
                        />
            case 'consent-management':
                return <SettingsItem
                            id='consent-management'
                            title={t(TK.consentManagement)}
                            contentComponentType={ConsentManagementSettings}
                            values={settings.consentManagement}
                            onSave={onSaveConsentManagement}
                            footerLeft={
                                <AppButton color="secondary" onClick={handleDownloadConsentPdf}>
                                    {t(TK.downloadConsentPdf)}
                                </AppButton>
                            }
                        />
            case 'clients-intelligent-categorization':
                return <SettingsItem
                            id='clients-intelligent-categorization'
                            title={t(TK.intelligentCategorization)}
                            contentComponentType={ClientsIntelligentCategorizationSettings}
                            values={settings.general}
                            onSave={onSaveGeneral}
                        />
            case 'invoices':
                return <SettingsItem
                            id='invoices'
                            title={t(TK.billingInvoices)}
                            contentComponentType={InvoicesSettings}
                            values={settings.invoices}
                            onSave={onSaveInvoices}
                            hideButtons
                        />
            case 'gohighlevel':
                return <SettingsItem
                            id='gohighlevel'
                            title='GoHighLevel'
                            contentComponentType={GoHighLevelSettings}
                            values={settings.integrations}
                            onSave={onSaveIntegrations}
                        />
            case 'mailerlite':
                return <SettingsItem
                            id='mailerlite'
                            title={t(TK.mailerLite)}
                            contentComponentType={MailerLiteSettings}
                            values={settings.integrations}
                            onSave={onSaveIntegrations}
                        />
            case 'webhook':
                return <SettingsItem
                            id='webhook'
                            title='Webhook'
                            contentComponentType={WebhookSettings}
                            values={settings.integrations}
                            onSave={onSaveIntegrations}
                        />
            case 'surveys':
                return <SettingsItem
                            id='surveys'
                            title={t(TK.surveys)}
                            contentComponentType={SurveysSettings}
                            values={null}
                            onSave={() => {}}
                            hideButtons
                        />
            case 'google-calendar':
                return <SettingsItem
                            id='google-calendar'
                            title={t(TK.googleSettingsTitle)}
                            contentComponentType={GoogleCalendarSettings}
                            values={null}
                            onSave={() => {}}
                            hideButtons
                        />
            case 'prescriptions':
                return <SettingsItem
                            id='prescriptions'
                            title={t(TK.prescriptions)}
                            contentComponentType={PrescriptionsSettings}
                            values={settings.prescriptionsSettings ?? {}}
                            onSave={onSavePrescriptions}
                        />
            case 'account':
                history.push(paths.account());
                return null;
            default:
                return null;
        }
    };

    return (
        <>
            <Breadcrumbs items={breadcrumbItems} />
            {renderContent()}
          
            <Modal isOpen={showProviderConfirm} toggle={() => setShowProviderConfirm(false)}>
                <ModalHeader toggle={() => setShowProviderConfirm(false)}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.invoiceProviderChangeWarning)}</ModalBody>
                <ModalFooter>
                    <AppButton color="secondary" onClick={() => setShowProviderConfirm(false)}>
                        {t(TK.cancel)}
                    </AppButton>
                    <AppButton color="success" onClick={() => {
                        dispatch(updateInvoicesSettings(pendingInvoiceSettings));
                        setShowProviderConfirm(false);
                        setPendingInvoiceSettings(null);
                    }}>
                        {t(TK.confirm)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default Settings;
